import { Injectable } from '@angular/core';
import { Odata, Utils } from '@mitel/cloudlink-sdk/utils';
import { Config, HttpResponse } from '@mitel/cloudlink-sdk';
import { environment } from 'src/environments/environment';
import { EntitiesCollection } from '../interfaces/collections';
import { Util } from '../utils/Util';

const LOCATION_ASSOCIATION_TYPE = 'urn:mitel:user-association:type:location';

export interface UserAssociation {
  accountId: string;
  userId: string;
  associationId: string;
  associatedEntityUri: string;
  associationType: string;
  displayName?: string;
  description?: string;
  createdBy?: string;
  createdOn?: string;
  modifiedBy?: string;
  modifiedOn?: string;
}

export interface LocationAssociation extends UserAssociation {
  locationId: string;
}

@Injectable({
  providedIn: 'root'
})
export class AssociationsService {

  constructor() { }

  async getUserAssociations(userId: string, odata?: Odata): Promise<EntitiesCollection<UserAssociation>> {
    return Config.httpService.get({
      url: Util.addQueryParamsToUrl(`${environment.adminURL}/users/${userId}/associations`, odata),
      headers: Config.appInfo.getAppHeaders()
    }).then((response: HttpResponse) => response.body as EntitiesCollection<UserAssociation>)
  }

  async getAllUserAssociations(userId: string, odata?: Odata): Promise<UserAssociation[]> {
    return new Promise<UserAssociation[]>(async (resolve, reject) => {
      let associations: UserAssociation[] = [];
      try {
        const associationCollection = await this.getUserAssociations(userId, odata);
        const next = Utils.getOdataNext(associationCollection);
        associations = Utils.getItemsFromCollection(associationCollection);
        if (next) {
          const moreAssociations = await this.getAllUserAssociations(userId, next);
          if (moreAssociations) {
            associations = associations.concat(moreAssociations);
          }
        }
        resolve(associations);
      } catch (err) {
        reject(err);
      }
    });
  }

  async getLocationAssociationForUser(userId: string): Promise<LocationAssociation> {
    return this.getAllUserAssociations(userId, { $Filter: `associationType eq '${LOCATION_ASSOCIATION_TYPE}'` })
      .then(associations => associations[0]) // There should only be one association with a location, so we return the first one
    .then((association: LocationAssociation) => {
      if (association) {
        association.locationId = this.getLocationFromAssociation(association);
      }
      return association;
    });
  }

  private getLocationFromAssociation(association: UserAssociation): string {
    return association?.associatedEntityUri?.slice(association.associatedEntityUri.lastIndexOf('/') + 1);
  }

  async createLocationAssociationForUser(userId: string, locationId: string): Promise<LocationAssociation> {
    let body = {
      associatedEntityUri: `user-association://locations/${locationId}`,
      associationType: LOCATION_ASSOCIATION_TYPE,
      displayName: `Location: ${locationId}`,
    }

    return Config.httpService.post({
      url: `${environment.adminURL}/users/${userId}/associations`,
      headers: Config.appInfo.getAppHeaders(),
      body
    })
    .then(response => response.body)
    .then((association: LocationAssociation) => {
      if (association) {
        association.locationId = this.getLocationFromAssociation(association);
      }
      return association;
    });
  }

  async updateLocationAssociationForUser(userId: string, associationId: string, locationId: string): Promise<LocationAssociation> {
    let body = {
      associatedEntityUri: `user-association://locations/${locationId}`,
      displayName: `Location: ${locationId}`,
    }

    return Config.httpService.put({
      url: `${environment.adminURL}/users/${userId}/associations/${associationId}`,
      headers: Config.appInfo.getAppHeaders(),
      body
    })
    .then((response: HttpResponse) => response.body)
    .then((association: LocationAssociation) => {
      if (association) {
        association.locationId = this.getLocationFromAssociation(association);
      }
      return association;
    });
  }

  async deleteUserAssociation(userId: string, associationId: string): Promise<void> {
    return Config.httpService.delete({
      url: `${environment.adminURL}/users/${userId}/associations/${associationId}`,
      headers: Config.appInfo.getAppHeaders()
    })
    .then(() => {}) // Shouldn't return anything on success
    .catch(err => {
      if (err?.statusCode == 404) {
        return;
      } else {
        throw err;
      }
    });
  }

  extractLocationAssociation(user: any): LocationAssociation | undefined {
    const associations = user?._embedded?.associations?._embedded?.items;
    const locationAssociation = associations?.find((association: any) =>
      association.associationType === LOCATION_ASSOCIATION_TYPE
    );

    if (locationAssociation) {
      // Add the locationId property using the existing helper method
      locationAssociation.locationId = this.getLocationFromAssociation(locationAssociation);
    }

    return locationAssociation;
  }
}
